import {
  Building2,
  LayoutDashboard,
  Calendar,
  Users,
  UserCog,
  FileText,
  Grid3X3,
  ClipboardList,
  Sparkles,
  MessageSquare,

  Ban,
  BarChart3,
  Coins,
  ClipboardCheck,
  Scale,
  CalendarOff,
  Timer,
  Wallet,
  CalendarClock,
  CalendarCheck,
  CalendarDays,
  Send,
  PieChart,
  Landmark,
  ReceiptText,
  Receipt,
  CreditCard,
  Settings2,
  Search,
  Package,
  Boxes,
  GraduationCap,
  BookOpen,
  TrendingUp,
  Megaphone,
  ShieldCheck,
  Tag,
  Radio,
  Link2,
} from 'lucide-react';
import { UserType } from '@/lib/auth/permissions';
import type { FeatureName, PermissionKey } from '@/lib/auth/permission-keys';
import { pageViewKey } from '@/lib/auth/page-registry';
import { reportViewKey } from '@/lib/auth/report-registry';
import { DASHBOARD_VIEW_NAV } from '@/components/dashboard/views';

// FeatureName now lives with the FE forward permission map (permission-keys.ts)
// so nav nodes and the resolved-key mapping share one vocabulary. Re-exported
// here for existing importers.
export type { FeatureName };

export interface NavNode {
  id: string;
  labelKey: string;
  icon?: React.ComponentType<{ className?: string }>;
  /** A branch may carry its own page — then it IS that page and needs no
   *  duplicate child. A branch without an href opens its first leaf instead. */
  href?: string;
  /** Label of the branch's own page row in the sidebar when it differs from
   *  the group name (CUS-08-01: group 'Customer list', page 'Manage Customer'). */
  pageLabelKey?: string;
  feature?: FeatureName;
  adminOnly?: boolean;
  /** HQ staff only (GEN-12-14): the person's outlet is HQ, not the ADMIN login. */
  hqOnly?: boolean;
  /** Shown to an admin login or an HQ staff member (Leave, Angela 22 Oct 2026). */
  adminOrHq?: boolean;
  /** Spec 12 — visible iff the user holds ANY of these resolved keys. Used to
   *  expose a single node (e.g. POS) to a grantable key without lighting up its
   *  whole feature. Combines with `feature`/`adminOnly` as an additional gate. */
  anyKey?: PermissionKey[];
  /** Manage Reports: this item IS a report. It shows iff the person holds
   *  `report.<reportId>.view`, which REPLACES the feature / adminOnly / anyKey
   *  gates it had (the report's default is exactly that old gate). */
  reportId?: string;
  /** Request 118: this item is a page with its own `page.<pageId>.view` right,
   *  which REPLACES the feature / adminOnly / anyKey gates it had (the page's
   *  default is exactly that old gate). */
  pageId?: string;
  /** "My Pay" is the non-admin counterpart of the /salary workspace. */
  hideForAdmin?: boolean;
  /** Hidden from an admin login and HQ staff (they use the managed page instead). */
  hideForAdminOrHq?: boolean;
  /** Leave managers only (admin, named leave managers, or HQ by default). */
  leaveManager?: boolean;
  /** Hidden from leave managers (they use the manager pages instead). */
  hideForLeaveManager?: boolean;
  /** Y-014: mediation visibility is a server probe, not a static role check. */
  requiresMediate?: boolean;
  /** Tabs (values of `?tab=`) that belong to this item's *section*, so the
   *  highlight follows every sub-tab, not just the one in `href`. e.g. the
   *  Appointments item stays active across Calendar/Active Treatments/History.
   *  When set, the item matches if the current `?tab` is in this list, or — when
   *  there is no `?tab` (the default tab) — if this item's own href carries no
   *  `?tab` (so the default section owns the bare URL). */
  matchTabs?: string[];
  /** SAL-24-02 report search: a group with `searchBox` shows a search box
   *  above its pages; a page's `aliases` are other names (Studio Manager /
   *  staging) the box also finds it by. */
  searchBox?: boolean;
  aliases?: string[];
  /** Other pages that belong to this item, so the menu stays open on them —
   *  e.g. the Leave tab row (Leave Balance, Leave Types …) all belong to
   *  Manage Company › Leave (Angela, 23 Oct 2026). Exact paths. */
  alsoPaths?: string[];
  children?: NavNode[];
  /** Angela, 22 Oct 2026 (as Leave): one sidebar entry whose pages are a row
   *  of tab buttons at the top of each page (NavTabs). Opens the first one. */
  tabs?: NavNode[];
}

/**
 * Angela's menu restructure (13 Aug 2026, FQ-3). Three main groups matching
 * her spreadsheet: Operation, Customers, Sales. Sub-groups within each.
 * Only existing features included per FQ-3 answer ("existing features only").
 *
 * Inventory and HR unchanged — not in her spreadsheet.
 */
export const NAV_TREE: NavNode[] = [
  {
    id: 'dashboard',
    labelKey: 'dashboard',
    icon: LayoutDashboard,
    href: '/dashboard',
    feature: 'dashboard',
    children: DASHBOARD_VIEW_NAV, // GEN-10: Outlet · Office · Management · Business
  },

  // ── Operation (1) — Angela's "Copy of Menu Proposal" ────────────────────────
  {
    id: 'operations',
    labelKey: 'operations',
    icon: Calendar,
    children: [
      {
        id: 'op-appointments',
        labelKey: 'schedule',
        icon: CalendarDays,
        children: [
          {
            id: 'appointments',
            pageId: 'appointments',
            labelKey: 'appointments',
            icon: CalendarDays,
            href: '/schedule?tab=staff-appointments',
            feature: 'schedule',
            // Therapists cannot access staff lanes; SchedulePage clamps this
            // target to Calendar. Keep the leaf active there so both ancestor
            // branches stay unfolded after that authorization redirect.
            matchTabs: ['staff-appointments', 'calendar', 'appointments'],
          },
          { id: 'questionnaires', pageId: 'questionnaires', labelKey: 'questionnaires', icon: ClipboardList, href: '/schedule?tab=active', feature: 'dailyReport', matchTabs: ['active'] },
          // Angela, 24 Oct 2026: the Daily Treatment Report sits under Schedule,
          // right after Questionnaires (it was under Reports).
          { id: 'daily-report', pageId: 'daily-report', labelKey: 'dailyReport', icon: ClipboardCheck, href: '/daily-report', feature: 'dailyReport' },
          // Angela, 24 Oct 2026: moved here from Settings (Admin only, as before).
          { id: 'rest-time', pageId: 'rest-time', labelKey: 'restTime', icon: Timer, href: '/operations/rest-time', adminOnly: true },
          { id: 'cancellation-reasons', pageId: 'cancellation-reasons', labelKey: 'cancellationReasons', icon: Ban, href: '/operations/cancellation-reasons', adminOnly: true },
        ],
      },
      // Angela, 21 Oct 2026: Operations' second menu is Schedule · Staff
      // Roster · Reports. Shift Types moved here from Settings (same page,
      // same Admin-only gate).
      {
        id: 'op-staff-roster',
        labelKey: 'staffRoster',
        icon: Users,
        children: [
          {
            id: 'staff-schedule',
            pageId: 'staff-schedule',
            labelKey: 'staffRoster',
            icon: Users,
            href: '/schedule?tab=staff-schedule',
            feature: 'canBook',
            matchTabs: ['staff-schedule', 'staff'],
          },
          { id: 'shift-types', pageId: 'shift-types', labelKey: 'shiftTypes', icon: CalendarDays, href: '/settings/shift-types', adminOnly: true },
          // OPS-14-09: who changed the roster.
          { id: 'roster-change-log', pageId: 'roster-change-log', labelKey: 'rosterChangeLog', icon: Users, href: '/roster/change-log', anyKey: ['staff.manage'] },
        ],
      },
      // One entry per report, not one entry onto a tab strip — Angela's menu
      // names each report, so the rail has to as well. The five-tab /reports
      // page still exists for existing links; it is just no longer the way in.
      {
        id: 'op-reports',
        labelKey: 'reports',
        icon: BarChart3,
        children: [
          // N-06 — one Appointments report with a customer-type filter, in
          // place of the former "by Outlet" / "New Customer" / "Existing
          // Customer" trio. The three old routes redirect onto it.
          { id: 'rpt-appointments', reportId: 'ops-appointments', labelKey: 'appointmentsReport', icon: BarChart3, href: '/reports/appointments', feature: 'reports' },
          { id: 'rpt-cancellations', reportId: 'ops-cancellations', labelKey: 'cancellationReport', icon: BarChart3, href: '/reports/cancellations', feature: 'reports' },
          { id: 'rpt-consultations', reportId: 'ops-consultations', labelKey: 'consultationReport', icon: BarChart3, href: '/reports/consultations', feature: 'reports' },
          { id: 'rpt-auto-usage', reportId: 'ops-auto-usage', labelKey: 'autoUsageByDate', icon: BarChart3, href: '/reports/auto-usage', feature: 'reports' },
          { id: 'rpt-working-hours', reportId: 'ops-working-hours', labelKey: 'workingHoursByStaff', icon: BarChart3, href: '/reports/working-hours', feature: 'reports' },
          // Customer Survey Report (OPS-25-03; Angela, 22 Oct 2026): date period, outlet,
          // customer; counts per rating.
          { id: 'rpt-questionnaire', reportId: 'ops-questionnaire', labelKey: 'questionnaireReport', icon: ClipboardList, href: '/reports/questionnaires', feature: 'dailyReport' },
          // Monthly Staff Duty Roster (OPS-37; Angela, 24 Oct 2026): who works when, month by month.
          // Gated like the Staff Roster it reads.
          { id: 'rpt-duty-roster', reportId: 'ops-duty-roster', labelKey: 'monthlyStaffDutyRoster', icon: CalendarDays, href: '/reports/duty-roster', feature: 'canBook' },
        ],
      },
    ],
  },

  // ── Customers (2) — Angela's "Copy of Menu Proposal" ──────────────────────
  {
    id: 'customers',
    labelKey: 'customers',
    icon: Users,
    children: [
      {
        id: 'customer-list',
        pageId: 'customer-list',
        labelKey: 'customerList',
        pageLabelKey: 'manageCustomer',
        icon: Users,
        href: '/customers',
        feature: 'customers',
        children: [
          { id: 'create-customer', pageId: 'create-customer', labelKey: 'createCustomer', icon: Users, href: '/customers/create', feature: 'customers' },
          { id: 'merge-customer', pageId: 'merge-customer', labelKey: 'mergeCustomer', icon: Users, href: '/customers/merge', feature: 'customers' },
          { id: 'customer-groups', pageId: 'customer-groups', labelKey: 'customerGroups', icon: Users, href: '/customers/groups', feature: 'customers' },
          // CUS-menu (Angela, 24 Oct 2026): Customer Fields sits right after Customer
          // Groups; the Configurations group is gone. Route unchanged.
          { id: 'cust-fields', pageId: 'cust-fields', labelKey: 'customerFields', icon: Settings2, href: '/customers/configurations/fields', feature: 'customers' },
        ],
      },
      {
        id: 'cust-forms',
        labelKey: 'forms',
        icon: FileText,
        children: [
          // CUS-05-14 / GEN-06-02: Form Templates · Fill a form · Form Responses.
          // Requests & Complaints is not an item of its own: it is a card in Fill a
          // form (HR-15-01) and opens under it.
          { id: 'form-templates', pageId: 'form-templates', labelKey: 'formTemplates', icon: FileText, href: '/customers/forms/templates', feature: 'customers', adminOnly: true },
          // GEN-06-02 — Fill a form: one card per published customer form.
          { id: 'cust-fill-a-form', pageId: 'cust-fill-a-form', labelKey: 'fillAForm', icon: FileText, href: '/customers/forms/fill', feature: 'customers' },
          { id: 'form-responses', pageId: 'form-responses', labelKey: 'formResponses', icon: FileText, href: '/customers/forms/responses', feature: 'customers' },
        ],
      },
      {
        id: 'cust-reports',
        labelKey: 'reports',
        icon: BarChart3,
        children: [
          { id: 'rpt-inactive', reportId: 'cust-inactive', labelKey: 'inactiveCustomers', icon: BarChart3, href: '/customers/reports/inactive', feature: 'reports' },
          { id: 'rpt-by-enrollment', reportId: 'cust-by-enrollment', labelKey: 'byEnrollmentDate', icon: BarChart3, href: '/customers/reports/by-enrollment', feature: 'reports' },
          { id: 'rpt-by-membership', reportId: 'cust-by-membership', labelKey: 'byMembership', icon: BarChart3, href: '/customers/reports/by-membership', feature: 'reports' },
          { id: 'rpt-by-gender', reportId: 'cust-by-gender', labelKey: 'byGender', icon: BarChart3, href: '/customers/reports/by-gender', feature: 'reports' },
          { id: 'rpt-by-age-group', reportId: 'cust-by-age-group', labelKey: 'byAgeGroup', icon: BarChart3, href: '/customers/reports/by-age-group', feature: 'reports' },
          { id: 'rpt-by-birthday', reportId: 'cust-by-birthday', labelKey: 'byBirthday', icon: BarChart3, href: '/customers/reports/by-birthday', feature: 'reports' },
          // Angela, 21 Oct 2026.
          { id: 'rpt-by-media', reportId: 'cust-by-media', labelKey: 'customersByMedia', icon: BarChart3, href: '/customers/reports/by-media', feature: 'reports' },
          { id: 'rpt-by-marketing', reportId: 'cust-by-marketing', labelKey: 'customersByMarketing', icon: BarChart3, href: '/customers/reports/by-marketing', feature: 'reports' },
        ],
      },
    ],
  },

  // ── Leads (GEN-09) — its own top menu (owner, 12 Oct: Leads stays top-level,
  // it does not move under Marketing). One group 'Follow-up', always open,
  // holding every list in the md order; the group name opens the Follow-up
  // screen (the Leads page until LEA-02/LEA-28 exist). Only the lists that
  // are built appear — Bal Pay joins this
  // list with its own branch, and Reports › List results with LEA-33.
  {
    id: 'leads',
    labelKey: 'leads',
    icon: Send,
    children: [
      // Angela, 24 Oct 2026: Leads = Lead Management (Follow-up + the lists) ·
      // Lead Settings (Lead Conditions · Message Templates) · Reports.
      {
        id: 'leads-management',
        labelKey: 'leadManagement',
        icon: Send,
        children: [
          { id: 'leads-follow-up', pageId: 'leads-follow-up', labelKey: 'followUp', icon: Send, href: '/customers/leads/follow-up', feature: 'customers' },
          { id: 'leads-new', pageId: 'leads-new', labelKey: 'leadsNew', icon: Send, href: '/customers/leads?tab=new', feature: 'customers', matchTabs: ['new'] },
          { id: 'leads-existing', pageId: 'leads-existing', labelKey: 'leadsExisting', icon: Send, href: '/customers/leads?tab=existing', feature: 'customers', matchTabs: ['existing'] },
          { id: 'leads-finishing', pageId: 'leads-finishing', labelKey: 'leadsFinishing', icon: Send, href: '/customers/leads?tab=finishing', feature: 'customers', matchTabs: ['finishing'] },
          { id: 'leads-inactive-package', pageId: 'leads-inactive-package', labelKey: 'leadsInactivePackage', icon: Send, href: '/customers/leads?tab=inactive-package', feature: 'customers', matchTabs: ['inactive-package'] },
          { id: 'leads-inactive-no-package', pageId: 'leads-inactive-no-package', labelKey: 'leadsInactiveNoPackage', icon: Send, href: '/customers/leads?tab=inactive-no-package', feature: 'customers', matchTabs: ['inactive-no-package'] },
          { id: 'leads-dnc', pageId: 'leads-dnc', labelKey: 'leadsDoNotContact', icon: Send, href: '/customers/leads?tab=dnc', feature: 'customers', matchTabs: ['dnc'] },
          { id: 'leads-vip', pageId: 'leads-vip', labelKey: 'leadsVip', icon: Send, href: '/customers/leads?tab=vip', feature: 'customers', matchTabs: ['vip'] },
          { id: 'leads-ycp', pageId: 'leads-ycp', labelKey: 'leadsYcp', icon: Send, href: '/customers/leads?tab=ycp', feature: 'customers', matchTabs: ['ycp'] },
        ],
      },
      {
        id: 'leads-settings-group',
        labelKey: 'leadSettings',
        icon: Send,
        children: [
          // Everyone with customers access can read them; only HQ edits (as on the pages).
          { id: 'leads-conditions', pageId: 'leads-conditions', labelKey: 'leadConditions', icon: Send, href: '/customers/leads/settings', feature: 'customers' },
          { id: 'leads-templates', pageId: 'leads-templates', labelKey: 'messageTemplates', icon: Send, href: '/customers/leads/templates', feature: 'customers' },
        ],
      },
      {
        id: 'leads-reports',
        labelKey: 'reports',
        icon: Send,
        children: [
          { id: 'leads-list-results', reportId: 'leads-list-results', labelKey: 'leadsListResults', icon: Send, href: '/customers/leads/reports/list-results', feature: 'customers' },
        ],
      },
    ],
  },

  // ── Marketing — MKT-01 (only the Form group is built; Leads Management,
  // Promotion, Media and Reports are other stacks' work) ────────────────────
  // ── Marketing — MKT-01-01, between Customers and Sales ───────────────────
  // MKT-03-03: Marketing = Marketing Activity (Promotion · Outlet Authority ·
  // Media) · Form · Reports. Promotion, Outlet Authority, Media and the Form group are built.
  {
    id: 'marketing',
    labelKey: 'marketing',
    icon: Megaphone,
    children: [
      {
        id: 'mkt-activity',
        labelKey: 'marketingActivity',
        icon: Tag,
        children: [
          // MKT-03-03: Marketing Activity = Promotion · Outlet Authority · Media.
          // Promotion is the MARKETING_ACTIVITY list with its details (MKT-01-02);
          // Outlet Authority and Media are built.
          { id: 'mkt-promotion', pageId: 'mkt-promotion', labelKey: 'promotion', icon: Tag, href: '/marketing/promotion', feature: 'customers' },
          { id: 'mkt-outlet-authority', pageId: 'mkt-outlet-authority', labelKey: 'outletAuthority', icon: ShieldCheck, href: '/marketing/outlet-authority', feature: 'customers' },
          { id: 'mkt-media', pageId: 'mkt-media', labelKey: 'media', icon: Radio, href: '/marketing/media', feature: 'customers' },
        ],
      },
      {
        id: 'mkt-form',
        labelKey: 'form',
        icon: FileText,
        children: [
          { id: 'mkt-form-templates', pageId: 'mkt-form-templates', labelKey: 'formTemplates', icon: FileText, href: '/marketing/forms/templates', adminOnly: true },
          { id: 'mkt-fill-a-form', pageId: 'mkt-fill-a-form', labelKey: 'fillAForm', icon: FileText, href: '/marketing/forms/fill', feature: 'customers' },
          { id: 'mkt-form-responses', pageId: 'mkt-form-responses', labelKey: 'formResponses', icon: FileText, href: '/marketing/forms/responses', feature: 'customers' },
        ],
      },
      {
        // MKT-02-03: the group lists the reports only (no item of its own).
        id: 'mkt-reports',
        labelKey: 'marketingReports',
        icon: BarChart3,
        children: [
          { id: 'mkt-rpt-enrollment-media', reportId: 'mkt-enrollment-media', labelKey: 'customerEnrollmentByMedia', icon: BarChart3, href: '/marketing/reports/customer-enrollment-by-media', feature: 'customers' },
          { id: 'mkt-rpt-enrollment-promotion', reportId: 'mkt-enrollment-promotion', labelKey: 'customerEnrollmentByPromotion', icon: BarChart3, href: '/marketing/reports/customer-enrollment-by-promotion', feature: 'customers' },
          { id: 'mkt-rpt-sales-media', reportId: 'mkt-sales-media', labelKey: 'salesByMedia', icon: BarChart3, href: '/marketing/reports/sales-by-media', feature: 'customers' },
          { id: 'mkt-rpt-sales-marketing', reportId: 'mkt-sales-marketing', labelKey: 'salesByMarketing', icon: BarChart3, href: '/marketing/reports/sales-by-marketing', feature: 'customers' },
        ],
      },
    ],
  },

  // ── Sales (3) — SAL-24 / SAL-14 menu ─────────────────────────────────────
  // Seven groups in Angela's order: Invoices, Treatment Usage, Credit Note,
  // Forms, Approvals, Payment Setup, Reports (SAL-24-02). Names and places
  // only — every screen keeps its own rules and permissions (SAL-24-07). There
  // is no 'POS' item or group any more (SAL-14-01): /finance/pos redirects to
  // New Invoice (src/routes.tsx). Items SAL-24 lists whose screens are not
  // built yet (Void Usage, Search Credit Notes, Form Templates / Fill a form /
  // Form Responses, Requests, the report search box) are left out rather than
  // pointing at nothing.
  //
  // Gating follows the endpoint each screen actually calls, not the group it
  // sits in: the till and the two invoice-writing flows are reachable with
  // `finance.create_sale`, while everything whose endpoint is `require_admin`
  // (credit notes, contra, preview) is admin-only — showing
  // it to a create_sale holder would only walk them into a 403 on save.
  {
    id: 'sales',
    labelKey: 'sales',
    icon: Landmark,
    children: [
      {
        id: 'sales-invoices',
        labelKey: 'invoices',
        icon: ReceiptText,
        children: [
          { id: 'create-invoice', pageId: 'create-invoice', labelKey: 'newInvoice', icon: ReceiptText, href: '/finance/pos/create-invoice', anyKey: ['finance.create_sale'] },
          // SAL-22-08 (Angela 14 Oct 2026) — every sign-in that can make an
          // invoice can take a balance payment: same key as New Invoice.
          { id: 'balance-payment', pageId: 'balance-payment', labelKey: 'balancePayment', icon: Wallet, href: '/finance/pos/balance-payment', anyKey: ['finance.create_sale'] },
          // Angela, 21 Oct 2026: more ways in — the same forms as Sales › Forms
          // and Invoice Details, each opening on its customer search.
          { id: 'inv-refund', pageId: 'inv-refund', labelKey: 'refundInvoice', icon: ReceiptText, href: '/finance/pos/refund', anyKey: ['finance.create_sale'] },
          { id: 'inv-upgrade', pageId: 'inv-upgrade', labelKey: 'upgradeInvoice', icon: ReceiptText, href: '/finance/pos/upgrade-invoice?mode=upgrade', anyKey: ['finance.create_sale'] },
          { id: 'inv-contra', pageId: 'inv-contra', labelKey: 'contraInvoice', icon: ReceiptText, href: '/finance/pos/contra-invoice', feature: 'finance', adminOnly: true },
          { id: 'search-invoices', pageId: 'search-invoices', labelKey: 'searchInvoices', icon: Search, href: '/finance/search-invoices', feature: 'finance', adminOnly: true },
        ],
      },
      {
        id: 'sales-usage',
        labelKey: 'treatmentUsage',
        icon: ClipboardCheck,
        children: [
          { id: 'search-usages', pageId: 'search-usages', labelKey: 'searchUsages', icon: Search, href: '/finance/search-usages', feature: 'finance', adminOnly: true },
          { id: 'create-usage', pageId: 'create-usage', labelKey: 'createUsage', icon: ClipboardCheck, href: '/finance/pos/create-usage', feature: 'finance', adminOnly: true },
          // SAL-12 — Void Usage (HQ approves in Sales › Approvals).
          { id: 'void-usage', pageId: 'void-usage', labelKey: 'voidUsage', icon: Ban, href: '/finance/pos/void-usage', feature: 'finance', adminOnly: true },
        ],
      },
      {
        id: 'sales-credit-note',
        labelKey: 'creditNote',
        icon: ReceiptText,
        children: [
          { id: 'credit-note', pageId: 'credit-note', labelKey: 'createCreditNote', icon: ReceiptText, href: '/finance/pos/credit-note', feature: 'finance', adminOnly: true },
          // SAL-40 — under Create Credit Note; read only.
          { id: 'search-credit-notes', pageId: 'search-credit-notes', labelKey: 'searchCreditNotes', icon: Search, href: '/finance/search-credit-notes', feature: 'finance', adminOnly: true },
          // Angela, 24 Oct 2026: moved here from Settings (Credit Rates page; Credit Type list).
          { id: 'credit-rates', pageId: 'credit-rates', labelKey: 'creditRates', icon: Coins, href: '/settings/credits', adminOnly: true },
          { id: 'credit-types', pageId: 'credit-types', labelKey: 'creditTypes', icon: Tag, href: '/finance/config/credit-types', adminOnly: true },
        ],
      },
      // SAL-27: the package forms (Contra, Upgrade, Extension, Refund) are
      // reached from Forms, in SAL-27-02's order.
      {
        id: 'sales-forms',
        labelKey: 'forms',
        icon: FileText,
        children: [
          // GEN-06-02 / SAL-27-02 — Form Templates · Fill a form · Form
          // Responses only. Contra, Upgrade, Extension and Refund
          // are cards on Fill a form, not menu items of their own (owner, 15
          // Oct 2026); their pages keep their addresses.
          { id: 'sales-form-templates', pageId: 'sales-form-templates', labelKey: 'formTemplates', icon: FileText, href: '/finance/forms/templates', adminOnly: true },
          { id: 'sales-fill-a-form', pageId: 'sales-fill-a-form', labelKey: 'fillAForm', icon: FileText, href: '/finance/forms/fill', anyKey: ['finance.create_sale', 'finance.manage', 'finance.view_all'] },
          { id: 'sales-form-responses', pageId: 'sales-form-responses', labelKey: 'formResponses', icon: FileText, href: '/finance/forms/responses', anyKey: ['finance.create_sale', 'finance.manage', 'finance.view_all'] },
        ],
      },
      {
        id: 'sales-approvals',
        labelKey: 'approvals',
        icon: ClipboardCheck,
        children: [
          // SAL-25 — the list of every request and where it is; every role (HR-23-07).
          { id: 'approval-requests', pageId: 'approval-requests', labelKey: 'approvalRequests', icon: ClipboardList, href: '/finance/approvals/requests', anyKey: ['finance.create_sale', 'finance.manage', 'finance.view_all', 'finance.approve_level1', 'finance.approve_hq'] },
          { id: 'approvals', pageId: 'approvals', labelKey: 'approvals', icon: ClipboardCheck, href: '/finance/approvals', anyKey: ['finance.approve_level1', 'finance.approve_hq'] },
          // Angela, 24 Oct 2026: Discount Approvers moved here from Settings › Dropdown Lists.
          { id: 'approval-settings', pageId: 'approval-settings', labelKey: 'approvalSettings', icon: Settings2, href: '/finance/config/approval-settings', adminOnly: true },
        ],
      },
      {
        id: 'sales-payment-setup',
        labelKey: 'paymentSetup',
        icon: Settings2,
        children: [
          { id: 'ihip', pageId: 'ihip', labelKey: 'ihipAlerts', icon: CalendarClock, href: '/finance/ihip', feature: 'finance', adminOnly: true },
          // SAL-09: the invoice's Payment Types (Full · IHIP · Maybank), next to the Modes.
          { id: 'payment-kinds', pageId: 'payment-kinds', labelKey: 'paymentKinds', icon: CreditCard, href: '/finance/config/payment-kinds', feature: 'finance', adminOnly: true },
          { id: 'payment-types', pageId: 'payment-types', labelKey: 'paymentModes', icon: CreditCard, href: '/finance/config/payment-types', feature: 'finance', adminOnly: true },
          { id: 'taxes', pageId: 'taxes', labelKey: 'taxes', icon: Receipt, href: '/finance/config/taxes', feature: 'finance', adminOnly: true },
        ],
      },
      // SAL-24-02 item 7: one flat list in A–Z order (by the English name), no
      // headings — the three former 'Unearned Revenues' reports included.
      {
        id: 'sales-reports',
        labelKey: 'reports',
        icon: PieChart,
        searchBox: true,
        children: [
          { id: 'rpt-ala-carte-by-beautician', reportId: 'sales-ala-carte-beautician', labelKey: 'alaCarteByBeautician', icon: BarChart3, href: '/finance/reports/ala-carte-by-beautician', feature: 'finance', adminOnly: true },
          { id: 'rpt-ala-carte-by-outlet', reportId: 'sales-ala-carte-outlet', labelKey: 'alaCarteByOutlet', icon: BarChart3, href: '/finance/reports/ala-carte-by-outlet', feature: 'finance', adminOnly: true },
          // SAL-32-01: Collection replaces Collection by Customer and Collection by
          // Payment Types in the menu; the old pages stay reachable by URL.
{ id: 'rpt-collection', reportId: 'sales-collection', labelKey: 'collection', icon: BarChart3, href: '/finance/reports/collection', feature: 'finance', adminOnly: true, aliases: ['Collection by Customer', 'Collection by Payment Types'] },
          { id: 'rpt-consultation-results', reportId: 'sales-consultation-results', labelKey: 'consultationResults', icon: BarChart3, href: '/finance/reports/consultation-results', feature: 'finance', adminOnly: true, aliases: ['Conversion by consultant', 'Why no package'] },
          { id: 'rpt-conversion', reportId: 'sales-conversion', labelKey: 'conversionReport', icon: BarChart3, href: '/finance/reports/conversion', feature: 'finance', adminOnly: true },
          { id: 'rpt-credit-notes', reportId: 'sales-credit-notes', labelKey: 'creditNotesReport', icon: BarChart3, href: '/finance/reports/credit-notes', feature: 'finance', adminOnly: true },
          { id: 'daily-close', pageId: 'daily-close', labelKey: 'dailyClose', icon: CalendarCheck, href: '/finance/daily-close', feature: 'finance', adminOnly: true },
{ id: 'rpt-expired-invoices', reportId: 'sales-expired-invoices', aliases: ['Outstanding invoices', 'Expired Invoices'], labelKey: 'expiredInvoicesReport', icon: BarChart3, href: '/finance/reports/expired-invoices', feature: 'finance', adminOnly: true },
{ id: 'rpt-discounts-refunds-voids', reportId: 'sales-discounts-refunds-voids', labelKey: 'discountsRefundsVoids', icon: BarChart3, href: '/finance/reports/discounts-refunds-voids', feature: 'finance', adminOnly: true },
          { id: 'rpt-gst', reportId: 'sales-gst', labelKey: 'gstReport', icon: BarChart3, href: '/finance/reports/gst', feature: 'finance', adminOnly: true },
          { id: 'rpt-item-sold-by-outlet', reportId: 'sales-item-sold-outlet', labelKey: 'itemSoldByOutlet', icon: BarChart3, href: '/finance/reports/item-sold-by-outlet', feature: 'finance', adminOnly: true },
          { id: 'rpt-items-sold-outlet-collection', reportId: 'sales-items-sold-outlet-collection', labelKey: 'itemsSoldByOutletWithCollection', icon: BarChart3, href: '/finance/reports/items-sold-outlet-collection', feature: 'finance', adminOnly: true },
          { id: 'rpt-money-due-risk', reportId: 'sales-money-due-risk', labelKey: 'moneyDueRisk', icon: BarChart3, href: '/finance/reports/money-due-risk', feature: 'finance', adminOnly: true, aliases: ['Outstanding invoices', 'Balance due', 'IHIP overdue', 'Expiring packages', 'Unearned'] },
          { id: 'rpt-sales-by-customer', reportId: 'sales-by-customer', labelKey: 'salesByCustomer', icon: BarChart3, href: '/finance/reports/sales-by-customer', feature: 'finance', adminOnly: true },
{ id: 'rpt-sales-by-staff', reportId: 'sales-by-staff', labelKey: 'salesByStaff', icon: BarChart3, href: '/finance/reports/sales-by-staff', feature: 'finance', adminOnly: true, aliases: ['Sales by consultant', 'Served by'] },
          { id: 'rpt-top-customers-by-limit', reportId: 'sales-top-customers', labelKey: 'topCustomersByLimit', icon: BarChart3, href: '/finance/reports/top-customers-by-limit', feature: 'finance', adminOnly: true },
          { id: 'rpt-top-sellers', reportId: 'sales-top-sellers', labelKey: 'topSellers', icon: BarChart3, href: '/finance/reports/top-sellers', feature: 'finance', adminOnly: true },
          { id: 'rpt-treatment-done-by-outlets', reportId: 'sales-treatment-done-outlets', labelKey: 'treatmentDoneByOutlets', icon: BarChart3, href: '/finance/reports/treatment-done-by-outlets', feature: 'finance', adminOnly: true },
          { id: 'finance-reports', reportId: 'sales-treatments-sold', aliases: ['Item Sold by Date', 'Treatments Sold', 'Packages Sold'], labelKey: 'treatmentsSold', icon: PieChart, href: '/finance/reports', feature: 'finance', adminOnly: true },
          { id: 'rpt-unearned-revenue', reportId: 'sales-unearned-revenue', aliases: ['Unearned Revenues', 'Unearned Revenue by Customer'], labelKey: 'unearnedRevenueReport', icon: BarChart3, href: '/finance/reports/unearned-revenue', feature: 'finance', adminOnly: true },
          // SAL-12-11 — after Unearned Revenue Report.
          { id: 'rpt-void-usages', reportId: 'sales-void-usages', labelKey: 'voidUsagesReport', icon: BarChart3, href: '/finance/reports/void-usages', feature: 'finance', adminOnly: true },
          { id: 'xero', pageId: 'xero', aliases: ['Xero', 'Outbox'], labelKey: 'xeroOutbox', icon: Send, href: '/finance/xero', feature: 'finance', adminOnly: true },
        ],
      },
    ],
  },

  // ── Inventory (INV-05-02) ──────────────────────────────────────────────────
  // Categories (Treatments · Products) · Inventory Control (Stock Movement).
  // Only screens that exist are listed; Inventory Level and Stocktake are added
  // when they are built.
  {
    id: 'inventory',
    labelKey: 'inventory',
    icon: Package,
    children: [
      {
        id: 'categories',
        labelKey: 'categories',
        children: [
          { id: 'treatments', pageId: 'treatments', labelKey: 'treatments', icon: Sparkles, href: '/treatments', feature: 'treatments' },
          // Angela, 24 Oct 2026: moved here from Settings, right after the treatments
          // (whose steps it maps). Admin only, as before.
          { id: 'program-step-mapping', pageId: 'program-step-mapping', labelKey: 'programStepMapping', icon: Link2, href: '/settings/program-step-mapping', adminOnly: true },
          { id: 'products', pageId: 'products', labelKey: 'products', icon: Package, href: '/inventory/products', feature: 'inventory' },
        ],
      },
      {
        id: 'inventory-control',
        labelKey: 'inventoryControl',
        children: [
          // INV-10-07: Inventory Level — the first item of Inventory Control.
          { id: 'inventory-level', pageId: 'inventory-level', labelKey: 'inventoryLevel', icon: Boxes, href: '/inventory/inventory-level', feature: 'inventory' },
          // The former 'Stock' screen, renamed (INV-04-11, INV-05-03).
          { id: 'stock', pageId: 'stock', labelKey: 'stockMovement', icon: Boxes, href: '/inventory/stock', feature: 'inventory' },
          // INV-09: Stocktake — count, compare, correct with a reason.
          { id: 'stocktake', pageId: 'stocktake', labelKey: 'stocktake', icon: ClipboardCheck, href: '/inventory/stocktake', feature: 'inventory', anyKey: ['inventory.manage'] },
        ],
      },
      {
        id: 'inventory-reports',
        labelKey: 'inventoryReports',
        icon: PieChart,
        children: [
          // INV-10: the movement history. GET /inventory/movements needs
          // `inventory.manage`, so only holders of that key see the item.
          { id: 'stock-movement-summary', reportId: 'inv-stock-movement', labelKey: 'stockMovementSummary', icon: BarChart3, href: '/inventory/reports/stock-movement-summary', feature: 'inventory', anyKey: ['inventory.manage'] },
          // INV-11: the reports, in INV-05-02's order.
          { id: 'low-stock-reorder', reportId: 'inv-low-stock', labelKey: 'lowStockReorder', icon: BarChart3, href: '/inventory/reports/low-stock-reorder', feature: 'inventory', anyKey: ['inventory.manage'] },
          { id: 'usage-vs-treatments', reportId: 'inv-usage-vs-treatments', labelKey: 'usageVsTreatments', icon: BarChart3, href: '/inventory/reports/usage-vs-treatments', feature: 'inventory', anyKey: ['inventory.manage'] },
          // INV-11-04: value at cost and at selling price — HQ only (cost prices).
          { id: 'stock-value', reportId: 'inv-stock-value', labelKey: 'stockValue', icon: BarChart3, href: '/inventory/reports/stock-value', feature: 'inventory', hqOnly: true },
          { id: 'loss-summary', reportId: 'inv-loss-summary', labelKey: 'lossSummary', icon: BarChart3, href: '/inventory/reports/loss-summary', feature: 'inventory', anyKey: ['inventory.manage'] },
          // INV-12-09: current Active / Inactive status and the changes in a period.
          { id: 'treatment-product-status', reportId: 'inv-treatment-product-status', labelKey: 'treatmentProductStatus', icon: BarChart3, href: '/inventory/reports/treatment-product-status', feature: 'inventory', anyKey: ['inventory.view'] },
        ],
      },
    ],
  },

  // ── HR (4) — Angela's "Copy of Menu Proposal" + relocated items ────────────
  {
    id: 'hr',
    labelKey: 'hr',
    icon: UserCog,
    children: [
      // Angela, 24 Oct 2026: Staff Directory is a group — Staff Directory · Roles & Permissions
      // (the roles page and its rights pages, moved from Settings).
      {
        id: 'staff-directory',
        pageId: 'staff-directory',
        labelKey: 'staffDirectory',
        icon: UserCog,
        href: '/staff',
        feature: 'staff',
        children: [
          { id: 'roles-permissions', labelKey: 'rolesPermissions', icon: ShieldCheck, href: '/settings/roles', adminOnly: true },
        ],
      },
      // HR-17: Leave is one entry; its pages are a row of buttons (LeaveTabs).
      {
        // Angela, 21 Oct 2026: 'Manage company' — Leave (one page whose tab row
        // holds Leave Calendar · Staff Leave Pending for Approval · Leave Balance …) and Time
        // attendance (the staff clock-in / clock-out report).
        id: 'leave',
        labelKey: 'manageCompany',
        icon: CalendarOff,
        children: [
          {
            id: 'leave-applications', labelKey: 'leave', icon: CalendarOff, href: '/leave/admin', feature: 'leave', leaveManager: true,
            // The Leave tab row (LeaveTabs): the menu stays open on every tab.
            alsoPaths: ['/leave', '/leave/types', '/settings/leave', '/leave/history', '/leave/approvers', '/leave/calendar', '/leave/ytd'],
          },
          { id: 'time-attendance', reportId: 'hr-attendance', labelKey: 'timeAttendance', icon: CalendarOff, href: '/attendance/report', adminOnly: true },
          // GEN-15: Audit Trail — HQ staff who hold the `audit.view` key (server checks both).
          { id: 'audit-trail', pageId: 'audit-trail', labelKey: 'auditTrail', icon: ShieldCheck, href: '/hr/audit-trail', hqOnly: true, anyKey: ['audit.view'] },
          // Angela, 22 Oct 2026: Payroll, Staff Claim, Staff Promotion and
          // Performance Review sit under Manage Company; each is one entry
          // whose pages are a tab row at the top (as Leave).
          {
            id: 'payroll',
            labelKey: 'payroll',
            tabs: [
              { id: 'salary', pageId: 'salary', labelKey: 'salary', icon: Wallet, href: '/salary', feature: 'salary', adminOnly: true },
              // Angela, 22 Oct 2026: HQ / admin only — her own commission is in My Profile › Payroll.
              { id: 'commission', pageId: 'commission', labelKey: 'commission', icon: Coins, href: '/commission', feature: 'commission', adminOrHq: true },
              { id: 'mediation', labelKey: 'mediation', icon: Scale, href: '/mediation', requiresMediate: true },
              // HR-18: Productivity is the last Payroll item (was top-level 'Performance').
              { id: 'productivity', reportId: 'hr-productivity', labelKey: 'productivity', icon: BarChart3, href: '/reports?tab=staff', feature: 'reports' },
            ],
          },
          // HR-20-01: Staff Claim right below Payroll. Her own claims are in
          // My Profile › Claims (Angela, 22 Oct 2026).
          {
            id: 'staff-claim',
            labelKey: 'staffClaim',
            icon: Receipt,
            tabs: [
              // Angela, 24 Oct 2026: office staff send their own claims here too.
              { id: 'claim-submission', pageId: 'claim-submission', labelKey: 'claimSubmission', icon: Receipt, href: '/hr/claims' },
              { id: 'claim-responses', pageId: 'claim-responses', labelKey: 'claimResponses', icon: Receipt, href: '/hr/claims/responses', hqOnly: true },
            ],
          },
          // HR-22-01: Staff Promotion right below Training — named so it is not
          // mixed up with Marketing › Promotion (sales promotions).
          {
            id: 'staff-promotion',
            labelKey: 'staffPromotion',
            icon: TrendingUp,
            tabs: [
              { id: 'promotion-status', pageId: 'promotion-status', labelKey: 'promotionStatus', icon: TrendingUp, href: '/staff-promotion/status', feature: 'staff' },
              { id: 'promotion-history', pageId: 'promotion-history', labelKey: 'promotionHistory', icon: ClipboardList, href: '/staff-promotion/history', feature: 'staff' },
              { id: 'promotion-template', pageId: 'promotion-template', labelKey: 'promotionTemplate', icon: ClipboardCheck, href: '/staff-promotion/template', feature: 'staff' },
            ],
          },
          // HR-19-01: Performance Review group after Training.
          {
            id: 'performance-review',
            labelKey: 'performanceReview',
            icon: ClipboardCheck,
            tabs: [
              { id: 'review-responses', pageId: 'review-responses', labelKey: 'reviewResponses', icon: BarChart3, href: '/performance-review/responses' },
            ],
          },
          // Angela, 24 Oct 2026: Outlets (details, hours for each day, special
          // operating hours, invoice expiry) is the last Manage Company item.
          { id: 'company-outlets', pageId: 'company-outlets', labelKey: 'companyOutlets', icon: Building2, href: '/company/outlets', adminOnly: true },
        ],
      },
      // Angela, 22 Oct 2026: My Profile — every person's own staff record,
      // read-only for staff — is its own HR item, after Manage Company. Her own
      // leave, pay and commission, claims and review are its tabs; Manage
      // Company keeps only the managing pages.
      // Angela, 22 Oct 2026: My Profile opens into her own record's sections in
      // the menu panel (the Staff Details tabs, plus Claims and Review).
      {
        id: 'my-profile',
        labelKey: 'myProfile',
        icon: UserCog,
        children: [
          { id: 'my-profile-user', labelKey: 'myProfileUser', icon: UserCog, href: '/my-profile?tab=user' },
          { id: 'my-profile-basic', labelKey: 'myProfileBasic', icon: UserCog, href: '/my-profile' },
          { id: 'my-profile-payroll', labelKey: 'myProfilePayroll', icon: UserCog, href: '/my-profile?tab=payroll' },
          { id: 'my-profile-leave', labelKey: 'myProfileLeave', icon: UserCog, href: '/my-profile?tab=leave' },
          { id: 'my-profile-leave-calendar', labelKey: 'myProfileLeaveCalendar', icon: UserCog, href: '/my-profile?tab=leaveCalendar' },
          // Outlet leave approvers only (Angela, 24 Oct 2026): pending leave of her own outlet.
          { id: 'my-profile-leave-approvals', labelKey: 'myProfileLeaveApprovals', icon: UserCog, href: '/my-profile?tab=leaveApprovals', leaveManager: true },
          { id: 'my-profile-contract', labelKey: 'myProfileContract', icon: UserCog, href: '/my-profile?tab=contract' },
          { id: 'my-profile-employment', labelKey: 'myProfileEmployment', icon: UserCog, href: '/my-profile?tab=employment' },
          { id: 'my-profile-skill', labelKey: 'myProfileSkill', icon: UserCog, href: '/my-profile?tab=skill' },
          { id: 'my-profile-documents', labelKey: 'myProfileDocuments', icon: UserCog, href: '/my-profile?tab=documents' },
          { id: 'my-profile-attendance', labelKey: 'myProfileAttendance', icon: UserCog, href: '/my-profile?tab=attendance' },
          { id: 'my-profile-review', labelKey: 'myProfileReview', icon: UserCog, href: '/my-profile?tab=review' },
          { id: 'my-profile-insurance', labelKey: 'myProfileInsurance', icon: UserCog, href: '/my-profile?tab=insurance' },
          { id: 'my-profile-claims', labelKey: 'myProfileClaims', icon: UserCog, href: '/my-profile?tab=claims' },
        ],
      },
      {
        id: 'meeting',
        labelKey: 'meeting',
        icon: FileText,
        children: [
          // HR-16-02: Meeting = Meeting Log only (Deck and Minutes are columns of it).
          // No feature gate: outlet staff open it read-only (owner, 24 Oct 2026);
          // the server decides what each sign-in gets back.
          { id: 'meeting-log', pageId: 'meeting-log', labelKey: 'meetingLog', icon: FileText, href: '/meetings/log' },
        ],
      },
      {
        id: 'training',
        labelKey: 'training',
        icon: GraduationCap,
        children: [
          // HR-16-02: Training = Training Log · Training Schedule · Training Status.
          { id: 'training-log', pageId: 'training-log', labelKey: 'trainingLog', icon: FileText, href: '/training/log', feature: 'staff' },
          { id: 'training-schedule', pageId: 'training-schedule', labelKey: 'trainingSchedule', icon: CalendarClock, href: '/training/schedule', feature: 'staff' },
          { id: 'training-matrix', pageId: 'training-matrix', labelKey: 'trainingStatus', icon: ClipboardList, href: '/training/matrix', feature: 'staff' },
          // SOP Library (Angela, 24 Oct 2026): HQ staff holding sop.view only.
          { id: 'training-sop-library', pageId: 'training-sop-library', labelKey: 'sopLibrary', icon: BookOpen, href: '/training/sop-library', hqOnly: true, anyKey: ['sop.view'] },
          // HR-14-06: moved here from the Inventory › Treatments tab.
        ],
      },
      {
        id: 'hr-forms',
        labelKey: 'forms',
        icon: FileText,
        children: [
          // GEN-06-02 / HR-10 — Form Templates · Fill a form · Form Responses
          // only; Damage Report and Compliment & Feedback are cards on
          // Fill a form (owner, 15 Oct 2026). Their pages keep their addresses.
          { id: 'hr-form-templates', pageId: 'hr-form-templates', labelKey: 'formTemplates', icon: FileText, href: '/hr/forms/templates', adminOnly: true },
          { id: 'hr-fill-a-form', pageId: 'hr-fill-a-form', labelKey: 'fillAForm', icon: FileText, href: '/hr/forms/fill' },
          // HR-10-04 — only HQ sign-ins open Form Responses.
          { id: 'hr-form-responses', pageId: 'hr-form-responses', labelKey: 'formResponses', icon: FileText, href: '/hr/forms/responses', adminOnly: true },
        ],
      },
      // GEN-12-01: Yakson AI under Forms — History, Q&A setup and Knowledge (the same three as the tab row), HQ roles only.
      {
        id: 'hr-yakson-ai',
        labelKey: 'yaksonAiGroup',
        icon: Sparkles,
        hqOnly: true,
        children: [
          { id: 'hr-ai-history', pageId: 'hr-ai-history', labelKey: 'aiHistory', icon: ClipboardList, href: '/hr/yakson-ai/history', hqOnly: true },
          { id: 'hr-ai-qa', pageId: 'hr-ai-qa', labelKey: 'aiQa', icon: MessageSquare, href: '/hr/yakson-ai/qa', hqOnly: true },
          { id: 'hr-ai-knowledge', pageId: 'hr-ai-knowledge', labelKey: 'aiKnowledge', icon: FileText, href: '/hr/yakson-ai/knowledge', hqOnly: true },
        ],
      },
    ],
  },
];

/**
 * The gating context pruneNav needs, injected by the caller (Sidebar). `canFeature`
 * is the resolved-permission-or-legacy resolver from useGating (ENG-YAKSON-080):
 * once /auth/my-permissions is loaded it reads the backend's resolved keys, and
 * before then (or on fetch failure) it falls back to the legacy userType matrix.
 * `userType` is still needed for the adminOnly/hideForAdmin semantics, which stay
 * userType-based per the A7 scope (feature-key gating is the migration target).
 */
export interface NavGate {
  userType: UserType;
  canMediate: boolean;
  /** HQ staff (GEN-12-14); drives `NavNode.hqOnly`. */
  isHq?: boolean;
  managesLeave?: boolean;
  canFeature: (feature: FeatureName) => boolean;
  /** Spec 12 — holds a specific key? Drives `NavNode.anyKey`. */
  canKey: (key: PermissionKey) => boolean;
  /** Spec 12 — can the user actually open this route? Lets pruneNav strip a
   *  node's href when its own page is not permitted (falls through to a child). */
  canRoute: (pathnameWithoutLocale: string) => boolean;
}

const isVisible = (node: NavNode, gate: NavGate): boolean => {
  if (node.reportId) return gate.canKey(reportViewKey(node.reportId));
  // A page that also groups other pages (Customer list) keeps its children when
  // closed: pruneNav drops only its own address (canRoute), not the group.
  if (node.pageId && !node.children) return gate.canKey(pageViewKey(node.pageId));
  if (node.requiresMediate) return gate.canMediate;
  if (node.adminOnly && gate.userType !== 'ADMIN') return false;
  if (node.hqOnly && !gate.isHq) return false;
  if (node.adminOrHq && gate.userType !== 'ADMIN' && !gate.isHq) return false;
  if (node.hideForAdminOrHq && (gate.userType === 'ADMIN' || gate.isHq)) return false;
  if (node.leaveManager && gate.userType !== 'ADMIN' && !gate.managesLeave) return false;
  if (node.hideForLeaveManager && (gate.userType === 'ADMIN' || gate.managesLeave)) return false;
  if (node.hideForAdmin && gate.userType === 'ADMIN') return false;
  if (node.feature && !gate.canFeature(node.feature)) return false;
  if (node.anyKey && !node.anyKey.some((k) => gate.canKey(k))) return false;
  return true;
};

/**
 * Prune the tree for this user. A branch survives only if it still leads
 * somewhere: it has its own page, or at least one surviving child. Without
 * this, a receptionist would see an empty "Sales" that opens onto nothing.
 */
export function pruneNav(nodes: NavNode[], gate: NavGate): NavNode[] {
  return nodes.reduce<NavNode[]>((acc, node) => {
    if (!isVisible(node, gate)) return acc;
    if (node.tabs) {
      const tabs = pruneNav(node.tabs, gate);
      if (tabs.length === 0) return acc;
      acc.push({ ...node, tabs, href: tabs[0].href, children: undefined });
      return acc;
    }
    const children = node.children ? pruneNav(node.children, gate) : undefined;
    const hasChildren = Boolean(children && children.length);
    const href = node.href && gate.canRoute(node.href) ? node.href : undefined;
    if (!href && !hasChildren) return acc;
    acc.push({ ...node, href, children: hasChildren ? children : undefined });
    return acc;
  }, []);
}

/** The page a click on this node should open: itself if it has one, else the
 *  first page reachable below it. Guarantees no click that only expands. */
export function firstTarget(node: NavNode): NavNode | null {
  if (node.href) return node;
  for (const child of node.children ?? []) {
    const found = firstTarget(child);
    if (found) return found;
  }
  return null;
}

export const flatten = (node: NavNode): NavNode[] => [
  node,
  ...(node.children ?? []).flatMap(flatten),
  ...(node.tabs ?? []).flatMap(flatten),
];

// ── GEN-04 header + sidebar helpers ─────────────────────────────────────────
// The header shows the top menus and the sidebar only the open menu's lower
// levels (GEN-04-02 / GEN-04-04), so both need the same answers: which top
// menus exist for this user, in which order, and which trail of nodes the
// current address belongs to. They live here so the two never disagree.

/** GEN-04-02: Dashboard · Operations · HR · Customers · Leads · Marketing ·
 *  Inventory · Sales. Leads is a top menu of its own (GEN-09, owner 12 Oct).
 *  Unknown ids keep their tree order after the known ones. */
export const TOP_MENU_ORDER = [
  'dashboard',
  'operations',
  'hr',
  'customers',
  'leads',
  'marketing',
  'inventory',
  'sales',
];

export function orderTopMenus(nodes: NavNode[]): NavNode[] {
  const rank = (node: NavNode) => {
    const i = TOP_MENU_ORDER.indexOf(node.id);
    return i === -1 ? TOP_MENU_ORDER.length : i;
  };
  return [...nodes].sort((a, b) => rank(a) - rank(b));
}

/** The pathname (locale stripped) and `?tab` that a node's href points at. */
const splitHref = (href: string): { path: string; tab: string | null } => {
  const [path, query] = href.split('?');
  return { path, tab: new URLSearchParams(query ?? '').get('tab') };
};

/**
 * Does this node's page match the current address exactly? Two nodes can share
 * a pathname and differ only by tab (Appointments is /schedule, Roster is
 * /schedule?tab=staff-schedule), so the tab takes part in the match or both
 * would light up at once. `matchTabs` lets one item own a whole section of
 * tabs (see NavNode.matchTabs).
 */
export function isNodeActive(node: NavNode, pathname: string, currentTab: string | null): boolean {
  if (node.tabs) return node.tabs.some((tab) => isNodeActive(tab, pathname, currentTab));
  if (!node.href) return false;
  if (node.alsoPaths?.includes(pathname)) return true;
  const { path, tab: wantTab } = splitHref(node.href);
  if (pathname !== path) return false;
  if (node.matchTabs) {
    if (currentTab) return node.matchTabs.includes(currentTab);
    // No ?tab = the default tab; the item whose href carries no tab owns it.
    return wantTab === null;
  }
  return wantTab ? currentTab === wantTab : !currentTab;
}

/**
 * The trail from the top menu down to the node that owns the current address,
 * for the header highlight, the open sidebar group and the path line.
 *
 * An exact match wins. A page that is not a menu item (Customer Details,
 * Appointment Details, Staff Profile … GEN-04-05) belongs to the menu item
 * whose path is the longest prefix of its address — so /customers/<id> keeps
 * Customers › Customer list lit. Empty when nothing matches (Settings).
 */
export function findActiveTrail(
  tree: NavNode[],
  pathname: string,
  currentTab: string | null,
): NavNode[] {
  let exact: NavNode[] | null = null;
  let best: NavNode[] = [];
  let bestLength = -1;

  const walk = (node: NavNode, ancestors: NavNode[]) => {
    const trail = [...ancestors, node];
    if (node.href) {
      if (!exact && isNodeActive(node, pathname, currentTab)) exact = trail;
      const { path } = splitHref(node.href);
      if (path !== '/' && pathname.startsWith(path + '/') && path.length > bestLength) {
        best = trail;
        bestLength = path.length;
      }
    }
    for (const child of node.children ?? []) walk(child, trail);
    for (const tab of node.tabs ?? []) walk(tab, trail);
  };
  for (const node of tree) walk(node, []);

  return exact ?? best;
}
